import { useInspectorMode } from "@utils/ContentfulUtils";
import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { getCookie } from "cookies-next";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import React, { FC, useId, useRef, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import { ctaClickTracking, pushButtonClick } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import useMetaverseRouteChangeHandler from "@/hooks/useMetaverse";
import { CustomLink, VideoModal } from "@/types";

const Modal = dynamic(() => import("@/components/ui/Modal/Modal"), { ssr: false });
const ModalContent = dynamic(() => import("@/components/ui/Modal/ModalContent"), { ssr: false });

interface Props {
  cta: CustomLink | VideoModal;
  btnClasses?: string;
  btnSize?: ButtonSize;
  showIcon?: boolean;
  iconBlack?: boolean;
  modalTitle?: string;
}

const isVideoModal = (cta: CustomLink | VideoModal): cta is VideoModal => cta.type === "videoModal";

const ButtonOrVideoModal: FC<Props> = ({ cta, btnClasses, showIcon, btnSize, iconBlack, modalTitle }) => {
  const videoID = useId();
  const t = useTranslations();
  const locale = useLocale();
  const { data: session } = useSession();
  const [modalOpen, setModalOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const currency = getCookie("currency") || "AED";
  useMetaverseRouteChangeHandler(isIframeLoaded);
  const [language, region] = locale.split("-");

  function getVideoUrlWithLangAndCurrency(url: string, language: string, currency: string): string {
    if (url.includes("www.youtube.com")) {
      return url;
    }
    try {
      const parsedUrl = new URL(url);
      const pathParts = parsedUrl.pathname.split("/");
      if (pathParts[1] === "ar" || pathParts[1] === "en" || pathParts[1] === "ru") {
        pathParts[1] = language || "en";
      } else {
        pathParts.splice(1, 0, language || "en");
      }
      parsedUrl.pathname = pathParts.join("/");
      parsedUrl.searchParams.set("currency", currency?.toLowerCase() || "aed");
      return parsedUrl?.href;
    } catch {
      return url;
    }
  }

  return (
    <>
      <Button
        {...(isVideoModal(cta)
          ? {
              onClick: () => {
                setModalOpen(true);
                ctaClickTracking(cta?.buttonLabel, "ButtonOrVideoModal", cta.buttonLabel);
              },
            }
          : {
              onClick: () => {
                pushButtonClick(cta.label, "ButtonOrVideoModal", !!session);
              },
              href: getLink(cta, locale),
            })}
        size={btnSize}
        customClass={btnClasses}
      >
        <span className="flex items-center gap-xs">
          {showIcon && <Icon className={cn(iconBlack ? "stroke-black-0" : "stroke-white")} name="play" size="16" title="play" />}
          {isVideoModal(cta) ? cta.buttonLabel : cta.label}
        </span>
      </Button>
      {isVideoModal(cta) && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <ModalContent className="h-dvh w-dvw !rounded-none pt-0 md:pt-m !px-0 md:!px-l">
            <div className={cn("flex items-center justify-between mb-l px-m md:px-0 pt-l")}>
              <p className={cn("font-primary uppercase")}>{modalTitle ?? cta.buttonLabel}</p>
              <Button
                className={"stroke-black"}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.TERTIARY}
                theme={ButtonTheme.BLACK}
                onClick={() => setModalOpen(false)}
              >
                <span className="pointer-events-none">
                  <Icon name="close" size="24" title={t("label.common.close")} />
                  <span className="sr-only">{t("button.label.closeModal")}</span>
                </span>
              </Button>
            </div>
            {cta.embeddedVideo ? (
              <iframe
                className={cn("object-cover w-full h-[calc(100dvh-72px)] md:rounded-lg border-0")}
                width="560"
                height="315"
                src={
                  cta?.embeddedVideo?.link?.url?.includes("view.propvr")
                    ? getVideoUrlWithLangAndCurrency(cta.embeddedVideo.link.url, language, currency)
                    : cta.embeddedVideo.link.url
                }
                onLoad={() => setIsIframeLoaded(true)}
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
              ></iframe>
            ) : (
              <video
                id={videoID}
                ref={videoRef}
                className={cn("object-cover w-full h-[calc(100dvh-72px)] max-h-[calc(100dvh-72px)] md:rounded-lg")}
                // src={isDesktop ? cta.video?.url : cta.mobileVideo?.url || cta.video?.url}
                controls
                autoPlay
                loop
                playsInline
                muted
              >
                <source src={isDesktop ? cta.video?.url : cta.mobileVideo?.url || cta.video?.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ButtonOrVideoModal;
